import { Controller } from "@hotwired/stimulus"
import WordList from "../models/wordlist"
import ScoreHistory from "../models/score_history"

export default class extends Controller {
  static values = {
    taboo: String,
    wordlength: { type: Number, default: 5 }
  }
  static outlets = ['guesses', 'scoring', 'message']

  initialize() {
    this.ready = false
    this.gameOver = false
    this._countdownTimeout = null
    this.wordlengthValue = this.resolveWordLength()
    this.initDyslexiaToggle()
    this.loadAndStart()
  }

  initDyslexiaToggle() {
    const toggle = document.getElementById('dyslexia-toggle')
    if (!toggle) return
    toggle.addEventListener('change', () => {
      document.body.classList.toggle('dyslexic', toggle.checked)
    })
  }

  async loadAndStart() {
    this.stopCountdown()
    this.wordlist = new WordList(this.wordlengthValue)
    await this.wordlist.ready

    this.guesses = []
    this.gameOver = false

    this.newGameButton = document.getElementById('new-game')
    this.leaderboardEl = document.getElementById('leaderboard')
    if (this.leaderboardEl) this.leaderboardEl.innerHTML = ''

    const session = ScoreHistory.loadSession()
    if (session && session.wordlength === this.wordlengthValue) {
      this.tabooValue = session.taboo
      this.guessesOutlet.setup(this.tabooValue)

      for (const guess of session.guesses) {
        this.guessesOutlet.restoreGuess(guess.word.split(''), guess.points, this.tabooValue)
        this.guesses.push(guess.word)
      }

      this.scoringOutlet.restore(session.scoring)
    } else {
      this.tabooValue = this.wordlist.selectRandomWord()
      this.guessesOutlet.setup(this.tabooValue)
      this.scoringOutlet.reset(this.wordlengthValue)
    }

    this.syncRadioButtons()
    this.bindNewGameButton()
    this.updateNewGameButton()
    this.ready = true
    this.dispatchLetterStates()
    this.updateMultiplierDisplay()
  }

  resolveWordLength() {
    const params = new URLSearchParams(window.location.search)
    const param = params.get('wordlength')
    if (param) {
      const parsed = parseInt(param, 10)
      if (WordList.hasWordlist(parsed)) return parsed
    }
    return this.wordlengthValue
  }

  syncRadioButtons() {
    const radio = document.getElementById(`wordlength_${this.wordlengthValue}`)
    if (radio) radio.checked = true
  }

  bindNewGameButton() {
    const form = document.querySelector('form:has(fieldset#wordlength)')
    if (!form) return

    form.addEventListener('submit', (e) => {
      if (this.guesses.length > 0 && !this.gameOver) {
        e.preventDefault()
        this.resign()
        this.updateNewGameButton()
      }
    })
  }

  updateNewGameButton() {
    if (!this.newGameButton) return
    this.newGameButton.value = (this.guesses.length > 0 && !this.gameOver)
      ? 'Surrender & Score'
      : 'New Game'
  }

  resign() {
    this.stopCountdown()
    this.guessesOutlet.revealTaboo(this.tabooValue)
    this.showMessage("You surrendered. The word was " + this.tabooValue + ".")
    this.gameOver = true
    this.saveAndShowLeaderboard()
  }

  startCountdown() {
    this.stopCountdown()
    const fibIndex = this.scoringOutlet.fibIndex
    const fill = Math.min(fibIndex, 12) / 12
    const duration = Math.max(0.1, 7.0 * (1.0 - fill))
    this.animateCountdownBar(duration)
    this._countdownTimeout = setTimeout(() => {
      if (this.scoringOutlet.fibIndex > 1) {
        this.scoringOutlet.penalize()
        this.scoringOutlet.renderTotal()
      } else {
        this.revealNextLetter()
      }
      this.updateMultiplierDisplay()
      if (!this.gameOver) this.startCountdown()
    }, (0.5 + duration) * 1000)
  }

  stopCountdown() {
    clearTimeout(this._countdownTimeout)
    this._countdownTimeout = null
    this.resetCountdownBar()
  }

  animateCountdownBar(duration) {
    const bar = document.getElementById('countdown-bar')
    if (!bar) return
    bar.style.animation = 'none'
    void bar.offsetWidth
    bar.style.animation = `countdown ${duration}s linear 0.5s both`
  }

  resetCountdownBar() {
    const bar = document.getElementById('countdown-bar')
    if (!bar) return
    bar.style.animation = 'none'
  }

  updateMultiplierDisplay() {
    const el = document.getElementById('multiplier-value')
    if (el) el.textContent = this.scoringOutlet.currentMultiplier
  }

  revealNextLetter() {
    const taboo = this.tabooValue.split('')
    const position = this.guessesOutlet.locked.findIndex(l => l === null)
    if (position === -1) { this.resign(); return }
    const letter = taboo[position]
    this.guessesOutlet.locked[position] = letter
    this.guessesOutlet.hooked[letter] = Math.max(this.guessesOutlet.hooked[letter] || 0, 1)
    this.guessesOutlet.revealLetter(position, letter)
    this.dispatchLetterStates()
    if (this.guessesOutlet.locked.every(l => l !== null)) this.resign()
  }

  computeLetterStates() {
    const states = {}
    for (const letter of this.guessesOutlet.locked) {
      if (letter != null) states[letter] = 'locked'
    }
    for (const letter of Object.keys(this.guessesOutlet.hooked)) {
      if (states[letter] !== 'locked') states[letter] = 'hooked'
    }
    return states
  }

  dispatchLetterStates() {
    window.dispatchEvent(new CustomEvent('letterStatesChanged', {
      detail: {
        letterStates: this.computeLetterStates(),
        useCounts: this.scoringOutlet.letterUseCounts
      }
    }))
  }

  saveSession(word, points) {
    const session = ScoreHistory.loadSession() || {
      taboo: this.tabooValue,
      wordlength: this.wordlengthValue,
      guesses: [],
      scoring: null
    }
    session.guesses.push({ word, points })
    session.scoring = this.scoringOutlet.snapshot()
    ScoreHistory.saveSession(session)
  }

  saveAndShowLeaderboard() {
    ScoreHistory.save(this.wordlengthValue, this.scoringOutlet.total, this.guesses.length)
    ScoreHistory.clearSession()
    this.showLeaderboard()
  }

  showLeaderboard() {
    if (!this.leaderboardEl) return

    const top = ScoreHistory.topTen(this.wordlengthValue)
    if (top.length === 0) return

    const currentScore = this.scoringOutlet.total
    const currentGuesses = this.guesses.length

    let html = '<h3>Top 10 for ' + this.wordlengthValue + '-letter words</h3><ol>'
    let matched = false
    for (const entry of top) {
      const isCurrentGame = !matched
        && entry.score === currentScore
        && entry.guessCount === currentGuesses
      if (isCurrentGame) matched = true

      html += `<li${isCurrentGame ? ' class="current"' : ''}>`
      html += `<span>${entry.score} pts</span>`
      html += `<span>${entry.guessCount} guess${entry.guessCount === 1 ? '' : 'es'}</span>`
      html += '</li>'
    }
    html += '</ol>'
    this.leaderboardEl.innerHTML = html
  }

  showMessage(text) {
    if (this.hasMessageOutlet) this.messageOutlet.update(text)
  }

  clearError() {
    this.guessesOutlet.clearError()
    this.showMessage('')
  }

  submit() {
    if (!this.ready || this.gameOver) return
    this.showMessage('')

    const wordArray = this.guessesOutlet.currentWordArray()
    const word = wordArray.join('')

    try {
      if (word === this.tabooValue) { throw new GuessedCorrectly() }

      this.guessesOutlet.validate(wordArray, this.wordlist, this.guesses)

      const classification = this.guessesOutlet.classify(wordArray, this.tabooValue)
      const points = this.scoringOutlet.compute(classification)
      this.scoringOutlet.add(points)

      this.guesses.push(word)
      this.guessesOutlet.acceptGuess(wordArray, points, this.tabooValue)
      this.updateNewGameButton()
      this.saveSession(word, points)
      this.dispatchLetterStates()
      // this.startCountdown() — disabled; timer removed per player feedback

    } catch (error) {
      if (error.name == "GuessedCorrectly") {
        this.stopCountdown()
        this.guesses.push(word)
        this.guessesOutlet.colorCurrentRow(wordArray, this.tabooValue)
        this.guessesOutlet.markCorrect()
        this.gameOver = true
        this.updateNewGameButton()
        this.saveAndShowLeaderboard()
      } else if (error.name === 'InvalidWord') {
        this.scoringOutlet.penalize()
        this.showMessage(error.message)
        this.guessesOutlet.markError()
      } else if (error.message) {
        this.showMessage(error.message)
        this.guessesOutlet.markError()
      } else {
        console.log("Error: ", error)
        this.guessesOutlet.markError()
      }
    } finally {
      this.scoringOutlet.renderTotal()
      this.updateMultiplierDisplay()
    }
  }
}

class GuessedCorrectly extends Error {
  constructor() {
    super("guessed correctly")
    this.name = "GuessedCorrectly"
  }
}
